import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { message, Alert } from '@brokerbay/design-components';
import { getJpmTransactionInfoKeyholder } from 'api/SupraController';
import type { IBrokerageDataWeb, IKeyholderDataWeb } from '@/app-package';
import { Modal, Color, Spin, Text, Title } from '@bb/elements-web';
import { useUpdatePaymentCardModalContext } from './UpdatePaymentCardModalContext';
import { useJpmcSessionForCardUpdate } from './hooks/useJpmcSessionForCardUpdate';

/** The URL to JPMC's JavaScript code. */
const hpfParentJS = `${process.env.JPMC_IFRAME_BASE_URL}js/hpfParent.min.js`;

declare global {
  interface Window {
    handlePaymentErrors?: (content: {
      messageId?: string;
      errorCode?: string[];
      subject?: string;
      reply?: boolean;
    }) => void;
    completePayment?: (txn: unknown) => void;
    cancelPayment?: (content: string) => void;
  }
}

interface UpdateJCardModalProps {
  /** Whether we should display the footer of the modal with the Terms & Conditions */
  showFooter?: boolean;
  /** Function to update the billing profile for the user or brokerage. */
  updateBillingProfile?: (
    profile: IKeyholderDataWeb | IBrokerageDataWeb,
    resetPaymentStatus?: boolean,
  ) => Promise<void>;
  /** The billing profile for the user or brokerage. */
  billingProfile?: IKeyholderDataWeb | IBrokerageDataWeb;
  /** Whether the billing profile is currently being updated. */
  isUpdatingProfile?: boolean;
}

/**
 * Renders the Chase (JPMorgan Chase) iframe in a modal.
 * – Shows a loader while the session‑id is being fetched by the useJpmcSessionForCardUpdate hook.
 * - If updateBillingProfile and billingProfile are provided, will immediately save the new card info to the billing profile upon successful completion in the iframe.
 *   If not provided, simply surfaces the updated payment info via context for the parent component to handle.
 *
 * 1. Loads the external hpfParent.min.js into the parent page *before* the iframe initialises.
 * 2. Exposes `handlePaymentErrors`, `completePayment`, and `cancelPayment` on
 *    `window` so the iframe can call them even though it is cross‑origin.
 * 3. Surfaces validation errors inside the modal
 * 4. Handles postMessage events from iframe (e.g., "what's this?" clicks)
 */
const UpdatePaymentCardModal: React.FC<UpdateJCardModalProps> = ({
  showFooter = false,
  billingProfile,
  updateBillingProfile,
  isUpdatingProfile,
}) => {
  // Get needed variables from the required Contexts.
  const { modalVisible, setModalVisible, setUpdatedPaymentInfo } =
    useUpdatePaymentCardModalContext();

  const { jpmcSessionId, isLoadingJPMCSession, jpmErrorMap, resetJpmcSession } =
    useJpmcSessionForCardUpdate({
      modalVisible,
      cardholderName: billingProfile?.CardholderName,
    });

  /** The external URL can be edited in the environment variables. */
  const jpmcIframeUrl = `${process.env.JPMC_IFRAME_BASE_URL}1_1/?`;
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [helpContent, setHelpContent] = useState<string | null>(null);

  /** Handler for closing the modal */
  const closeAndResetIframe = useCallback(() => {
    setIframeError(null);
    setHelpContent(null);
    resetJpmcSession();
    setModalVisible(false);
  }, [resetJpmcSession, setModalVisible]);

  // Load hpfParent.min.js and register the global callbacks *once per open*.
  useEffect(() => {
    if (!modalVisible || !jpmcSessionId) return;

    let isMounted = true;

    // Inject the helper script
    const scriptId = 'hpf-parent-js';
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id = scriptId;
      s.src = hpfParentJS;
      s.async = false; // important
      document.head.appendChild(s);
    }

    // Handle postMessage events from the iframe
    const handlePostMessage = (event: MessageEvent) => {
      // Verify origin for security - ensure it's from the JPMC iframe
      const jpmcBaseUrl = process.env.JPMC_IFRAME_BASE_URL;
      if (jpmcBaseUrl) {
        const jpmcHostname = new URL(jpmcBaseUrl).hostname;

        if (event.origin !== `https://${jpmcHostname}`) {
          return;
        }
      }

      // Handle structured postMessage objects
      if (event.data && typeof event.data === 'object') {
        const { subject, content } = event.data;

        if (subject === 'whatsThis') {
          const helpText =
            'CVC (Card Verification Code) is the three or four-digit security code on the back of your card.';

          if (content === 'cvv') {
            setHelpContent(helpText);
          }
        }
      }
    };

    window.addEventListener('message', handlePostMessage);

    // Expose callback for HPF validation errors.
    window.handlePaymentErrors = ({ errorCode }) => {
      let friendly = 'Unable to update card.';

      if (errorCode && errorCode.length > 0) {
        const friendlyMessagesList = errorCode
          .map(code => jpmErrorMap[code]) // get the error strings
          .filter(Boolean); // get rid of nulls

        const uniqueFriendlyMessages = [...new Set(friendlyMessagesList)];

        if (uniqueFriendlyMessages.length > 0) {
          friendly = uniqueFriendlyMessages.join('\n');
        }
      }
      setIframeError(friendly);
    };

    // Expose callback for successful completion.
    let completePaymentInProgress = false;
    window.completePayment = () => {
      // Not declaring completePayment async to avoid DataCloneError when JPMC's code
      // tries to trigger a postMessage with the return value.
      if (completePaymentInProgress) return;
      completePaymentInProgress = true;

      (async () => {
        try {
          const jpmcInfo = await getJpmTransactionInfoKeyholder({
            JPMCUid: jpmcSessionId,
          });

          if (!isMounted) return;

          if (!jpmcInfo) {
            message.error('Failed to fetch JPMC transaction info');
            return;
          }

          setUpdatedPaymentInfo({
            JPUid: jpmcSessionId,
            ccNumber: jpmcInfo.CCNumber,
            maskedCCNumber: jpmcInfo.MaskedCCNumber,
            ccExpiryDate: jpmcInfo.CCExpiryDate,
            cardholderName: jpmcInfo.CardholderName,
            ccType: jpmcInfo.CardType,
          });

          if (updateBillingProfile && billingProfile && isMounted) {
            // If updateBillingProfile() is provided, immediately save the new card info.

            // Make the call to BillingPlatform, saving the card info.
            await updateBillingProfile(
              {
                ...billingProfile,
                JPUid: jpmcSessionId,
                CCNumber: jpmcInfo.CCNumber,
                MaskedCCNumber: jpmcInfo.MaskedCCNumber,
                CCExpiryDate: jpmcInfo.CCExpiryDate,
                CardholderName: jpmcInfo.CardholderName,
                CCType: jpmcInfo.CardType,
              },
              true,
            );

            message.success('Card updated.');
          }
        } catch (e) {
          message.error('Failed to update card');
        } finally {
          closeAndResetIframe();
        }
      })();
    };

    window.cancelPayment = () => {
      closeAndResetIframe();
    };

    return () => {
      isMounted = false;
      window.removeEventListener('message', handlePostMessage);
      delete window.handlePaymentErrors;
      delete window.completePayment;
      delete window.cancelPayment;
    };
  }, [
    modalVisible,
    jpmcSessionId,
    jpmErrorMap,
    closeAndResetIframe,
    billingProfile,
    updateBillingProfile,
    setUpdatedPaymentInfo,
  ]);

  return (
    <Modal
      dataCy="jpmc-update-card-modal"
      title={
        <Title dataCy="update-card-modal-title" level={4} margin={0}>
          Update Card
        </Title>
      }
      open={modalVisible}
      onCancel={() => closeAndResetIframe()}
      footer={null}
      width={420}
      padding={24}
    >
      {isLoadingJPMCSession || !jpmcSessionId || isUpdatingProfile ? (
        <StyledSpin dataCy="loading-jpmc-modal-spinner" spinning size="large" />
      ) : null}

      {iframeError && !isUpdatingProfile && (
        <Alert
          type="error"
          message={iframeError}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      {helpContent && !isUpdatingProfile && (
        <Alert
          type="info"
          message="Help Information"
          description={helpContent}
          closable
          onClose={() => setHelpContent(null)}
          style={{ marginBottom: 16, whiteSpace: 'pre-line' }}
        />
      )}

      <IframeWrapper>
        {!isLoadingJPMCSession && jpmcSessionId && !isUpdatingProfile && (
          <iframe
            key={jpmcSessionId ?? 0}
            id="jpmc-frame"
            src={`${jpmcIframeUrl}uID=${jpmcSessionId}`}
            width="100%"
            height="175"
            title="Chase payment iframe"
          />
        )}

        {showFooter && (
          <Text>
            By updating your payment information, you authorize Supra to charge
            your balances on your billing days per subscription and once every
            plan cycle until cancelled. The card saved will be the default
            payment method used for every charge. Your recurring payment is not
            refundable. Missed payments are subject to a fee.{' '}
            <StyledTOCLink
              href="https://www.brokerbay.com/supraone-user-terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </StyledTOCLink>
          </Text>
        )}
      </IframeWrapper>
    </Modal>
  );
};

const IframeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;

const StyledSpin = styled(Spin)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
`;

const StyledTOCLink = styled.a`
  font-family: inherit;
  font-weight: bold;

  && {
    text-decoration: underline;
    color: ${Color.PRIMARY_TEXT};
  }
`;

export default UpdatePaymentCardModal;
